import { BootResponse, EPopupSubType, OfferAPIResponse } from '@appcharge/shared-ui';
import { Offer } from 'common/contracts';
import { ERewardCalendarPopUpTrigger, ERewardCalendarRewardCollection } from 'constants/enums';
import { REWARD_CALENDAR_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';

import type { OffersPreviewAPIResponse } from '../previewResponse.types';

export type {
  OffersPreviewAPIResponse,
  PreviewBalanceItem
} from '../previewResponse.types';

type OfferUiWithSubType = Offer['offerUi'] & { offerUiSubType?: string };

const DEFAULT_OFFER_API_RESPONSE: Omit<OfferAPIResponse, 'offers'> = {
  balances: [],
  offerFocus: {},
  profileFrameImg: '',
  playerLevelImg: '',
  publisherSettings: {},
  bannerImage: '',
  playerLevels: [],
  tabs: [],
  logo: ''
};

function normalizePopupForPreview(popup: Offer): Offer {
  const offerUi = (popup.offerUi ?? {}) as OfferUiWithSubType;
  const subType = popup.subType ?? offerUi.offerUiSubType;

  const isRewardCalendar = subType === EPopupSubType.REWARD_CALENDAR;

  const needsOfferUiSubType = subType != null && offerUi.offerUiSubType == null;
  const needsOfferUiRewardCalendar =
    isRewardCalendar && !(offerUi as any).rewardCalendar;

  const normalizedOfferUi =
    needsOfferUiSubType || needsOfferUiRewardCalendar
      ? ({
          ...offerUi,
          ...(needsOfferUiSubType ? { offerUiSubType: subType } : {}),
          ...(needsOfferUiRewardCalendar
            ? {
                rewardCalendar: {
                  backgroundColor: REWARD_CALENDAR_DEFAULTS.backgroundColor,
                  dayFontColor: REWARD_CALENDAR_DEFAULTS.dayFontColor,
                  collectibleBadgeImage:
                    REWARD_CALENDAR_DEFAULTS.collectibleBadgeImage,
                  lockedBadgeImage: REWARD_CALENDAR_DEFAULTS.lockedBadgeImage,
                  collectIconImage: REWARD_CALENDAR_DEFAULTS.collectIconImage,
                  lockedIconImage: REWARD_CALENDAR_DEFAULTS.lockedIconImage
                }
              }
            : {})
        } as Offer['offerUi'])
      : (offerUi as Offer['offerUi']);

  const normalizedRewardCalendar =
    isRewardCalendar && !popup.rewardCalendar
      ? {
          popupTrigger: ERewardCalendarPopUpTrigger.SHOW_AUTOMATICALLY,
          rewardCollectionBehavior: ERewardCalendarRewardCollection.FORCE_PLAYER_TO_COLLECT
        }
      : popup.rewardCalendar;

  return {
    ...popup,
    ...(normalizedRewardCalendar !== popup.rewardCalendar
      ? { rewardCalendar: normalizedRewardCalendar }
      : {}),
    offerUi: normalizedOfferUi
  };
}

export function mapPreviewResponseToOfferAPIResponse(
  response: Record<string, unknown>
): OfferAPIResponse {
  const preview = response as OffersPreviewAPIResponse;
  const offers = preview.offers ?? {};
  const popups = (offers.popups ?? []).map(normalizePopupForPreview);
  const balances = (preview.balances ?? []).map((balance) => ({
    ...balance,
    quantity: balance.quantity ?? '',
    formattedAmount: balance.formattedAmount ?? balance.formattedQuantity ?? ''
  }));

  return {
    ...DEFAULT_OFFER_API_RESPONSE,
    offers: {
      popups,
      promotions: offers.promotions ?? [],
      bundles: offers.bundles ?? [],
      rolling: offers.rolling ?? [],
      progressBars: offers.progressBars ?? []
    },
    balances,
    sections: preview.sections ?? [],
    offerFocus: preview.offerFocus ?? {},
    profileFrameImg: preview.profileFrameImg ?? '',
    playerLevelImg: preview.playerLevelImg ?? '',
    publisherSettings: preview.publisherSettings ?? {},
    bannerImage: preview.bannerImage ?? '',
    playerLevels: preview.playerLevels ?? [],
    tabs: preview.tabs ?? [],
    logo: preview.logo ?? ''
  };
}

export function mergePreviewStoreThemeIntoBoot(
  boot: BootResponse,
  response: Record<string, unknown>
): BootResponse {
  const preview = response as OffersPreviewAPIResponse;
  const storeTheme = preview.storeTheme;
  if (!storeTheme || !boot.storeTheme?.general) {
    return boot;
  }

  const general = boot.storeTheme.general;
  const storeScreen = boot.storeTheme.storeScreen;
  const bgDesktop =
    storeTheme.bgImageDesktop?.trim() || general.backgroundImageDesktop;
  const bgMobile =
    storeTheme.bgImageMobile?.trim() || general.backgroundImageMobile;
  const logo = preview.logo?.trim() || general.logo;
  const defaultBanner =
    preview.bannerImage != null
      ? String(preview.bannerImage).trim()
      : storeScreen?.defaultBanner ?? '';

  return {
    ...boot,
    storeTheme: {
      ...boot.storeTheme,
      general: {
        ...general,
        backgroundImageDesktop: bgDesktop,
        backgroundImageMobile: bgMobile,
        logo
      },
      storeScreen: storeScreen
        ? {
            ...storeScreen,
            defaultBanner
          }
        : boot.storeTheme.storeScreen
    }
  };
}
